/* ==========================================================================
   Cart add-on (shipping protection) — replaces the UpCart add-on module.
   Impact build (Nordspur UK / Nordspur DE).

   Keeps one toggle row in step with the cart:
     - switched on by default (configurable) once the cart has real items;
     - switching it off removes the line and records the choice as a cart
       attribute, so it is not silently re-added on the next page;
     - the row hides itself while the cart holds nothing else.

   THEME COUPLING (all of it lives in the ThemeBridge object below):
     - cart routes           Shopify.routes.root
     - redraw the drawer     'cart:prepare-bundled-sections' -> write with
                             sections -> 'cart:change'
     - the cart PAGE is not ajax in Impact: it reloads, exactly as the theme's
       own quantity control does.
   ========================================================================== */

(function () {
  'use strict';

  var OPT_OUT = 'shipping_protection_declined';

  // The row renders in BOTH the drawer and the cart page, so two instances can
  // be live at once. Cart writes share one lock, or they would both decide the
  // add-on is missing and each add it.
  var LOCK = { busy: false };

  var ThemeBridge = {
    root: function () {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    },

    isCartPage: function () {
      var vars = window.themeVariables;
      return !!(vars && vars.settings && vars.settings.pageType === 'cart');
    },

    getCart: function () {
      return fetch(ThemeBridge.root() + 'cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); });
    },

    /* Ask every cart-aware section to name itself, so one write redraws them. */
    bundledSections: function () {
      var sections = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sections }
        })
      );
      return sections;
    },

    post: function (path, body) {
      return fetch(ThemeBridge.root() + path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(body)
      }).then(function (r) { return r.json(); });
    },

    /* Hand the redrawn sections back to the theme. */
    announce: function (cart) {
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'line-item:change', cart: cart }
        })
      );
    }
  };

  class CartAddon {
    constructor(el) {
      this.el = el;
      this.variantId = Number(el.dataset.variantId);
      this.autoAdd = el.dataset.autoAdd === 'true';
      this.toggle = el.querySelector('[data-cart-addon-toggle]');

      this.toggle.addEventListener('click', this.onToggle.bind(this));

      this.refresh();
    }

    refresh(attempt) {
      var tries = attempt || 0;
      var self = this;

      // 1. Waiting, not bailing. A row bound while a cart write was in flight
      //    used to stay hidden for good, because the later cart:refresh only
      //    binds elements it has not seen before.
      if (LOCK.busy) {
        if (tries > 40) return Promise.resolve();
        return new Promise(function (resolve) {
          window.setTimeout(function () { resolve(self.refresh(tries + 1)); }, 150);
        });
      }

      // 2. Retrying, not swallowing. /cart.js can answer 429 or an HTML
      //    challenge page instead of JSON; getCart() then rejects, the empty
      //    catch ate it, and the row stayed hidden while the add-on was still
      //    in the cart and still being charged for. That is the shape of
      //    "it disappeared for some reason".
      return ThemeBridge.getCart()
        .then(this.sync.bind(this))
        .catch(function () {
          if (tries > 5) return;
          return new Promise(function (resolve) {
            window.setTimeout(function () { resolve(self.refresh(tries + 1)); }, 400 * (tries + 1));
          });
        });
    }

    /* Lines that are not the add-on itself. */
    otherItems(cart) {
      var id = this.variantId;
      return cart.items.filter(function (i) { return i.variant_id !== id; });
    }

    addonLine(cart) {
      var id = this.variantId;
      return cart.items.find(function (i) { return i.variant_id === id; }) || null;
    }

    declined(cart) {
      return (cart.attributes || {})[OPT_OUT] === 'yes';
    }

    sync(cart) {
      if (LOCK.busy) return;

      var line = this.addonLine(cart);
      var others = this.otherItems(cart);

      // Nothing else in the cart: hide the row, and never leave the add-on alone.
      if (others.length === 0) {
        this.el.hidden = true;
        if (line) this.remove(line);
        return;
      }

      this.el.hidden = false;
      this.setChecked(!!line);

      if (!line && this.autoAdd && !this.declined(cart)) {
        this.add();
      }
    }

    setChecked(on) {
      this.toggle.setAttribute('aria-checked', on ? 'true' : 'false');
      this.el.classList.toggle('is-on', on);
    }

    onToggle() {
      if (LOCK.busy) return;
      var on = this.toggle.getAttribute('aria-checked') === 'true';

      if (on) {
        ThemeBridge.getCart().then(function (cart) {
          var line = this.addonLine(cart);
          if (line) this.remove(line, true);
        }.bind(this));
      } else {
        this.add(true);
      }
    }

    add(optOut) {
      this.write(
        ThemeBridge.post('cart/add.js', { items: [{ id: this.variantId, quantity: 1 }] }),
        optOut === undefined ? undefined : ''
      );
    }

    remove(line, optOut) {
      this.write(
        ThemeBridge.post('cart/change.js', { id: line.key, quantity: 0 }),
        optOut === undefined ? undefined : 'yes'
      );
    }

    /* Every cart write ends with one update.js that records the shopper's
       choice (when they made one) AND asks for the redrawn sections back, so
       the drawer refreshes from the server in a single round trip. */
    write(operation, optOut) {
      LOCK.busy = true;
      this.el.classList.add('is-busy');

      var self = this;

      return operation
        .then(function () {
          var body = { sections: ThemeBridge.bundledSections().join(',') };
          if (optOut !== undefined) body.attributes = attrs(OPT_OUT, optOut);
          return ThemeBridge.post('cart/update.js', body);
        })
        .then(this.done.bind(this))
        .catch(function () { self.failed(); });
    }

    done(cart) {
      LOCK.busy = false;
      this.el.classList.remove('is-busy');

      // Impact does not ajax the cart page; its own quantity control reloads,
      // so we do the same and the page comes back correct in one step.
      if (ThemeBridge.isCartPage()) {
        window.location.reload();
        return;
      }

      ThemeBridge.announce(cart);
      this.sync(cart);
    }

    failed() {
      LOCK.busy = false;
      this.el.classList.remove('is-busy');
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }
  }

  function attrs(key, value) {
    var attributes = {};
    attributes[key] = value;
    return attributes;
  }

  var ADDON_INSTANCES = new WeakMap();

  function init() {
    document.querySelectorAll('[data-cart-addon]').forEach(function (el) {
      if (el.dataset.addonReady) {
        // Bound already, but the cart has moved on since. Re-sync it, or a row
        // that was hidden during an in-flight write never comes back.
        var existing = ADDON_INSTANCES.get(el);
        if (existing) existing.refresh();
        return;
      }
      el.dataset.addonReady = '1';
      ADDON_INSTANCES.set(el, new CartAddon(el));
    });
  }

  // The drawer replaces its own markup on every cart change, so the row is a
  // new element each time and has to be bound again.
  document.addEventListener('cart:change', function () { window.setTimeout(init, 0); });
  document.addEventListener('cart:refresh', function () { window.setTimeout(init, 50); });
  document.addEventListener('variant:add', function () { window.setTimeout(init, 0); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ==== cart parity stepper (Thrives) ==== */
/* ==========================================================================
   CART PARITY — the − / + buttons.

   Appended to cart-addon.js, which the cart drawer already loads.

   These buttons deliberately do NOT talk to the cart. They write a new value
   into the theme's own `<input is="quantity-input">` and fire `change`; the
   theme's quantity component does the rest, exactly as if the shopper had
   typed the number. That keeps one code path to the cart — the theme's —
   rather than a second one of ours that could disagree with it about limits,
   stock rules or the section re-render.

   Delegated from the document because the cart drawer replaces its own
   contents on every update, so anything bound directly to a button would
   stop working after the first change.
   ========================================================================== */
(function () {
  'use strict';

  function step(input, delta) {
    var current = parseInt(input.value, 10);
    if (isNaN(current)) current = 0;

    var next = current + delta;

    // Respect the limits the theme put on the input: never below zero, never
    // past a max that came from real stock (inventory_policy: deny).
    var min = 0;
    var maxAttr = input.getAttribute('max');
    var max = maxAttr ? parseInt(maxAttr, 10) : null;
    if (next < min) next = min;
    if (max !== null && !isNaN(max) && next > max) next = max;
    if (next === current) return;

    input.value = String(next);
    // `change` is what the theme's quantity-input listens for. bubbles so the
    // component catches it wherever it sits in the tree.
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-parity-step]');
    if (!button) return;

    var stepper = button.closest('line-item-quantity');
    var input = stepper && stepper.querySelector('input.quantity-input');
    if (!input) return;

    event.preventDefault();
    step(input, Number(button.dataset.parityStep) || 0);
  });
})();
